import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import './ViewSaleModalStyles.css';
import apiService from '../../services/api';
import AddPurchase from './AddPurchase';
import AddPurchaseReturn from './AddPurchaseReturn';
import AddPurchaseRequisition from './AddPurchaseRequisition';
import PurchaseOrder from './PurchaseOrder';
import PurchaseRequisition from './PurchaseRequisition';
import ListPurchaseReturn from './ListPurchaseReturn';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import invoiceGenerator from '../../services/invoiceGenerator';
import LabelGenerator from './LabelGenerator';
import AddPaymentModal from './AddPaymentModal';
import ViewPaymentsModal from './ViewPaymentsModal';
import UpdatePurchaseStatusModal from './UpdatePurchaseStatusModal';
import PurchaseNotificationModal from './PurchaseNotificationModal';

const Purchases = ({ activeTab: propActiveTab }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('list_purchases');

  // Synchronize state with prop changes from dashboard popover
  useEffect(() => {
    if (propActiveTab) {
      if (propActiveTab === 'purchases') {
        setActiveTab('list_purchases');
      } else {
        setActiveTab(propActiveTab);
      }
    }
  }, [propActiveTab]);

  // Shared State
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // List Purchases State
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [openActionId, setOpenActionId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'transaction_date', direction: 'desc' });
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [productsForLabels, setProductsForLabels] = useState([]);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showViewPaymentsModal, setShowViewPaymentsModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [isLoadingView, setIsLoadingView] = useState(false);

  // Fetch Purchases
  useEffect(() => {
    if (activeTab === 'list_purchases') {
      fetchPurchases();
    }
  }, [activeTab, currentPage, perPage, searchTerm]);

  const fetchPurchases = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching purchases...');
      
      let locId = null;
      let bizId = null;
      
      try {
        const userDetails = await apiService.getUserDetails();
        const userData = userDetails.data || userDetails;
        setLoggedInUser(userData);
        locId = userData.location_id;
        bizId = userData.business_id;
      } catch (err) {
        console.warn('Could not fetch user details for filtering:', err);
      }

      const formatForApi = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const now = new Date();
      const startDateDefault = new Date();
      startDateDefault.setFullYear(now.getFullYear() - 2); // 2 years ago

      const params = { 
        page: currentPage,
        per_page: 100, // Fetch more records now that it's working
        start_date: formatForApi(startDateDefault),
        end_date: formatForApi(now),
        search: searchTerm 
      };
      
      if (locId) params.location_id = locId;
      if (bizId) params.business_id = bizId;

      console.log('Final request params:', params);
      const data = await apiService.getPurchases(params);
      console.log('Purchases API response:', data);
      
      // Robust mapping to handle various API response formats (Success wrapper, Laravel pagination, etc.)
      let purchaseList = [];
      if (data.success && data.data) {
        purchaseList = Array.isArray(data.data) ? data.data : (data.data.data || []);
      } else {
        purchaseList = Array.isArray(data) ? data : (data.data || []);
        // Check for double nesting common in paginated results
        if (!Array.isArray(purchaseList) && purchaseList.data) {
          purchaseList = purchaseList.data;
        }
      }
      
      console.log('Mapped purchase list:', purchaseList);
      setPurchases(Array.isArray(purchaseList) ? purchaseList : []);
      
      // API returns { current_page, data: [...], last_page, total, ... }
      // Pagination info is at the TOP level of the response, not inside data.data
      if (data.last_page) {
        setLastPage(data.last_page);
        setTotalPurchases(data.total);
      } else if (data.meta?.last_page) {
        setLastPage(data.meta.last_page);
        setTotalPurchases(data.meta.total);
      }
    } catch (err) {
      console.error('Failed to load purchases', err);
      setPurchases([]);
    } finally {
      setIsLoading(false);
    }
  };

  const resolveAddedBy = (purchase) => {
    // 1. Try created_by_user object (Relation)
    if (purchase.created_by_user) {
      return `${purchase.created_by_user.first_name || ''} ${purchase.created_by_user.last_name || ''}`.trim() || purchase.created_by_user.username;
    }
    
    // 2. Try created_by object (Flattened or alternate name)
    if (typeof purchase.created_by === 'object' && purchase.created_by) {
      return `${purchase.created_by.first_name || ''} ${purchase.created_by.last_name || ''}`.trim() || purchase.created_by.username;
    }

    // 3. Session Fallback: If IDs match
    if (loggedInUser && (purchase.created_by === loggedInUser.id || purchase.created_by_id === loggedInUser.id)) {
      return `${loggedInUser.first_name || ''} ${loggedInUser.last_name || ''}`.trim() || loggedInUser.username;
    }

    // 4. Other fields
    return purchase.added_by_name || purchase.created_by_name || 'N/A';
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedPurchases = (list) => {
    if (!sortConfig.key) return list;
    return [...list].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortIcon = ({ colKey }) => {
    if (sortConfig.key !== colKey) {
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M7 15l5 5 5-5" /><path d="M7 9l5-5 5 5" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M18 15l-6-6-6 6" />
      </svg>
    ) : (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M6 9l6 6 6-6" />
      </svg>
    );
  };

  const handleExport = (format) => {
    if (!purchases || purchases.length === 0) {
      alert('No data to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['Action', 'Date', 'Reference No', 'Location', 'Supplier', 'Purchase Status', 'Payment Status', 'Grand Total', 'Payment Due', 'Added By'];
    const rows = purchases.map(p => [
      '', // Action placeholder for export
      p.transaction_date || 'N/A',
      p.ref_no || 'N/A',
      p.location?.name || 'N/A',
      p.supplier?.name || p.contact?.name || 'N/A',
      p.status || 'Pending',
      p.payment_status || 'Due',
      parseFloat(p.final_total || 0).toFixed(2),
      `P: ${parseFloat(p.payment_due || 0).toFixed(2)}${p.return_transaction ? ` / R: ${parseFloat(p.return_transaction.amount_due || 0).toFixed(2)}` : ''}`,
      resolveAddedBy(p)
    ]);

    const title = 'Purchases List';
    const filename = 'purchases';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const buildPurchaseInvoiceData = (purchase) => ({
    invoiceNumber: purchase.invoice_no || purchase.ref_no || '--',
    invoiceDate: purchase.transaction_date ? new Date(purchase.transaction_date).toLocaleDateString() : '--',
    customerName: purchase.contact?.name || purchase.supplier || '--',
    items: (purchase.purchase_lines || []).map(line => ({
      sku: line.product?.sku || line.sub_sku || '',
      name: line.product?.name || ('Product #' + line.product_id),
      quantity: parseFloat(line.quantity || 0),
      unitPrice: parseFloat(line.purchase_price || 0),
      total: parseFloat(line.quantity || 0) * parseFloat(line.purchase_price || 0),
    })),
    payments: (purchase.payment_lines || []).map(p => ({
      method: p.method,
      amount: parseFloat(p.amount || 0),
      date: p.paid_on && !p.paid_on.startsWith('0000') ? new Date(p.paid_on).toLocaleDateString() : '--',
    })),
    subtotal: parseFloat(purchase.total_before_tax || purchase.final_total || 0),
    discount: parseFloat(purchase.discount_amount || 0),
    tax: parseFloat(purchase.tax_amount || 0),
    total: parseFloat(purchase.final_total || 0),
    totalPaid: (purchase.payment_lines || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0),
    amountDue: parseFloat(purchase.payment_due || 0),
    notes: purchase.additional_notes || '',
  });

  const handlePrint = (purchase) => {
    if (purchase && purchase.id) {
      invoiceGenerator.printInvoicePDF(buildPurchaseInvoiceData(purchase));
      setOpenActionId(null);
    } else {
      printPage();
      setShowExportDropdown(false);
    }
  };
  const handleColumnVisibility = () => { alert('Column visibility settings'); setShowExportDropdown(false); };

  const handleAdd = () => {
    if (activeTab === 'list_purchases') {
      setSelectedPurchase(null);
      setActiveTab('add_purchase');
    }
    else if (activeTab === 'purchase_order') navigate('/purchases/order/add');
    else if (activeTab === 'purchase_requisition') setActiveTab('add_purchase_requisition');
    else if (activeTab === 'purchase_return') setActiveTab('add_purchase_return');
  };

  const handleOpenLabels = (purchase) => {
    if (!purchase.purchase_lines || purchase.purchase_lines.length === 0) {
      alert('No products found in this purchase to generate labels.');
      return;
    }

    const mappedProducts = purchase.purchase_lines.map(line => ({
      id: line.product_id,
      name: line.product?.name || `Product #${line.product_id}`,
      sku: line.product?.sku || line.variations?.sub_sku || '--',
      price: parseFloat(line.variations?.sell_price_inc_tax || 0),
      priceExcTax: parseFloat(line.variations?.default_sell_price || 0),
      variation: line.variations?.name || 'Default',
      numberOfLabels: Math.ceil(parseFloat(line.quantity || 1)),
      barcode: line.product?.sku || line.variations?.sub_sku || ''
    }));

    setProductsForLabels(mappedProducts);
    setShowLabelModal(true);
    setOpenActionId(null);
  };

  const handleView = async (purchase) => {
    setOpenActionId(null);
    setSelectedPurchase(purchase);
    setShowViewModal(true);
    setIsLoadingView(true);
    try {
      const response = await apiService.getPurchase(purchase.id);
      let fullPurchase = response.data || response;
      if (Array.isArray(fullPurchase)) fullPurchase = fullPurchase[0];
      setSelectedPurchase(fullPurchase);
    } catch (err) {
      console.warn('Could not fetch full purchase for view, using list data:', err);
    } finally {
      setIsLoadingView(false);
    }
  };

  const handleEdit = async (purchase) => {
    try {
      setIsLoading(true);
      // Fetch fresh details for this specific purchase to ensure full relation data
      const response = await apiService.getPurchase(purchase.id);
      console.log('[handleEdit] Raw getPurchase response for id', purchase.id, ':', response);
      console.log('[handleEdit] response.data:', response.data);
      console.log('[handleEdit] response.data?.purchase_lines:', response.data?.purchase_lines);

      // API returns { data: { id, purchase_lines, ... } }  — unwrap .data first
      let fullPurchase = response.data || response;

      // Also handle array wrapper e.g. [ { id, purchase_lines, ... } ]
      if (Array.isArray(fullPurchase)) {
        fullPurchase = fullPurchase[0];
      }

      console.log('[handleEdit] fullPurchase being set as editData:', fullPurchase);
      console.log('[handleEdit] purchase_lines count:', fullPurchase?.purchase_lines?.length);
      
      setSelectedPurchase(fullPurchase);
      setActiveTab('add_purchase');
    } catch (err) {
      console.warn('Error fetching full purchase details:', err);
      // Fallback to the current purchase object if individual fetch fails
      setSelectedPurchase(purchase);
      setActiveTab('add_purchase');
    } finally {
      setIsLoading(false);
      setOpenActionId(null);
    }
  };

  const handleAddPayment = (purchase) => {
    setSelectedPurchase(purchase);
    setShowAddPaymentModal(true);
    setOpenActionId(null);
  };

  const handleViewPayments = (purchase) => {
    setSelectedPurchase(purchase);
    setShowViewPaymentsModal(true);
    setOpenActionId(null);
  };

  const handleUpdateStatus = (purchase) => {
    setSelectedPurchase(purchase);
    setShowUpdateStatusModal(true);
    setOpenActionId(null);
  };

  const handleShowNotification = (purchase) => {
    setSelectedPurchase(purchase);
    setShowNotificationModal(true);
    setOpenActionId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this purchase?')) {
      try {
        await apiService.deletePurchase(id);
        alert('Purchase deleted successfully');
        fetchPurchases();
      } catch (err) {
        alert('Failed to delete purchase: ' + err.message);
      }
    }
    setOpenActionId(null);
  };

  if (activeTab === 'add_purchase') {
    return (
      <div className="products-container">
        <AddPurchase 
          onBack={() => {
            setActiveTab('list_purchases');
            setSelectedPurchase(null);
            fetchPurchases(); // Correct function name to refresh list
          }} 
          editData={selectedPurchase}
        />
      </div>
    );
  }

  if (activeTab === 'add_purchase_return') {
    return (
      <div className="products-container">
        <AddPurchaseReturn onBack={() => setActiveTab('purchase_return')} />
      </div>
    );
  }

  if (activeTab === 'add_purchase_requisition') {
    return (
      <div className="products-container">
        <AddPurchaseRequisition onBack={() => setActiveTab('purchase_requisition')} />
      </div>
    );
  }

  if (activeTab === 'purchase_order') {
    return <PurchaseOrder />;
  }

  if (activeTab === 'purchase_requisition') {
    return <PurchaseRequisition />;
  }

  if (activeTab === 'purchase_return') {
    return <ListPurchaseReturn />;
  }

  return (
    <div className="products-container">
      <div className="main-card">
        {/* Tabs & Search Header */}


        {/* Table Controls */}
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)} title="Open Filters">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={handleAdd}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        {/* Table Content */}
        {activeTab === 'list_purchases' && (
          isLoading ? (
            <div className="loading-sales">
              <h3>⏳ Loading Purchases...</h3>
              <p>Fetching purchases data from server...</p>
            </div>
          ) : purchases.length === 0 ? (
            <div className="no-sales">
              <h3>📊 No Purchases Found</h3>
              <p>No purchases have been recorded yet.</p>
            </div>
          ) : (
            <>
              <div className="products-table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      {[
                        { key: 'transaction_date', label: 'Date' },
                        { key: 'ref_no', label: 'Reference No' },
                        { key: 'location', label: 'Location' },
                        { key: 'supplier', label: 'Supplier' },
                        { key: 'status', label: 'Purchase Status' },
                        { key: 'payment_status', label: 'Payment Status' },
                        { key: 'final_total', label: 'Grand Total' },
                        { key: 'payment_due', label: 'Payment Due' },
                        { key: 'added_by', label: 'Added By' },
                      ].map(({ key, label }) => (
                        <th key={key} className="sortable" onClick={() => handleSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem' }}>
                            {label}
                            <SortIcon colKey={key} />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedPurchases(purchases).map(purchase => (
                      <tr key={purchase.id}>
                        <td className="actions-cell">
                          <div className="action-dropdown-container">
                            <button className="action-btn-trigger" onClick={() => setOpenActionId(openActionId === purchase.id ? null : purchase.id)}>
                              Actions <span className="arrow-down">▼</span>
                            </button>
                            {openActionId === purchase.id && (
                               <div className="action-dropdown-menu">
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleView(purchase)}
                                 >
                                   👁️ View
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handlePrint(purchase)}
                                 >
                                   🖨️ Print
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleEdit(purchase)}
                                 >
                                   ✏️ Edit
                                 </button>
                                 <button
                                   className="dropdown-item delete-item"
                                   onClick={() => handleDelete(purchase.id)}
                                 >
                                   🗑️ Delete
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleOpenLabels(purchase)}
                                 >
                                   🏷️ Labels
                                 </button>
                                 
                                 <div className="divider"></div>
                                 
                                 {purchase.payment_status?.toLowerCase() !== 'paid' && (
                                   <button
                                     className="dropdown-item"
                                     onClick={() => handleAddPayment(purchase)}
                                   >
                                     💳 Add Payment
                                   </button>
                                 )}
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleViewPayments(purchase)}
                                 >
                                   💰 View Payments
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleUpdateStatus(purchase)}
                                 >
                                   ✅ Update Status
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => handleShowNotification(purchase)}
                                 >
                                   ✉️ Items Pending Notification
                                 </button>
                                 <button
                                   className="dropdown-item"
                                   onClick={() => alert('Purchase Return functionality coming soon!')}
                                 >
                                   ↩️ Purchase Return
                                 </button>
                               </div>
                             )}
                          </div>
                        </td>
                        <td>{purchase.transaction_date || 'N/A'}</td>
                        <td>{purchase.ref_no || 'N/A'}</td>
                        <td>{purchase.location?.name || 'N/A'}</td>
                        <td>{purchase.supplier?.name || purchase.contact?.name || 'N/A'}</td>
                        <td><span className={`status-badge ${purchase.status?.toLowerCase()}`}>{purchase.status || 'Pending'}</span></td>
                        <td><span className={`status-badge ${purchase.payment_status?.toLowerCase()}`}>{purchase.payment_status || 'Due'}</span></td>
                        <td>${parseFloat(purchase.final_total || 0).toFixed(2)}</td>
                        <td className="payment-due-cell">
                          <div style={{ fontSize: '0.85rem' }}>
                            <span style={{ color: '#64748b' }}>P:</span> ${parseFloat(purchase.payment_due || 0).toFixed(2)}
                          </div>
                          {purchase.return_transaction && parseFloat(purchase.return_transaction.amount_due) > 0 && (
                            <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>
                              <span style={{ color: '#ef4444' }}>R:</span> ${parseFloat(purchase.return_transaction.amount_due).toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td>{resolveAddedBy(purchase)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination (Simplified for now) */}
              {purchases.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                  <div>Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalPurchases || purchases.length)} of {totalPurchases || purchases.length} entries</div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                    <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>Page {currentPage} of {lastPage}</span>
                    <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))} disabled={currentPage === lastPage}>Next</button>
                  </div>
                </div>
              )}
            </>
          )
        )}

        {/* Placeholders for other tabs */}
        {activeTab !== 'list_purchases' && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
            <h3>{activeTab.replace(/_/g, ' ').toUpperCase()}</h3>
            <p>Content for {activeTab.replace(/_/g, ' ')} will be displayed here.</p>
            <p>Check "List Purchases" tab for the implemented layout.</p>
            {/* Reuse table structure for visual consistency even if empty */}
            <div className="products-table-container" style={{ marginTop: '1rem' }}>
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Reference No</th>
                    <th>Date</th>
                    <th>Supplier</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Grand Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>No data available for this section yet.</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* Filter Modal (Shared) */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                <div className="filter-group"><label>Status:</label><select><option value="all">All Status</option></select></div>
                <div className="filter-group"><label>Payment Status:</label><select><option value="all">All</option></select></div>
                <div className="filter-group"><label>Supplier:</label><select><option value="all">All</option></select></div>
                <div className="filter-group"><label>Location:</label><select><option value="all">All</option></select></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
              <button className="modal-btn primary-btn">Apply Filters</button>
            </div>
          </div>
        </div>
      )}
      {/* View Purchase Modal */}
      {showViewModal && selectedPurchase && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-container invoice-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Purchase Details ( Reference No: {selectedPurchase.ref_no} )</h2>
              <button className="modal-close-btn" onClick={() => setShowViewModal(false)}>
                ✕
              </button>
            </div>
            
            <div className="modal-content invoice-content">
              {/* Invoice Header */}
              <div className="invoice-header-section">
                <div className="invoice-info-item">
                  <strong>Reference No:</strong> #{selectedPurchase.ref_no}
                </div>
                <div className="invoice-info-item">
                  <strong>Date:</strong> {selectedPurchase.transaction_date}
                </div>
                <div className="invoice-info-item">
                  <strong>Purchase Status:</strong> {selectedPurchase.status}
                </div>
                <div className="invoice-info-item">
                  <strong>Payment Status:</strong> {selectedPurchase.payment_status}
                </div>
                <div className="invoice-info-item">
                  <strong>Location:</strong> {selectedPurchase.location?.name || 'Lefty\'S Liquor'}
                </div>
              </div>

              {/* Supplier Info */}
              <div className="customer-service-section">
                <div className="customer-info-box">
                  <div className="info-item">
                    <strong>Supplier:</strong> {selectedPurchase.contact?.name || selectedPurchase.supplier?.name || 'N/A'}
                  </div>
                  <div className="info-item">
                    <strong>Business:</strong> {selectedPurchase.contact?.supplier_business_name || 'Lefty\'S Liquor'}
                  </div>
                  <div className="info-item">
                    <strong>Address:</strong>
                    <div>
                      {selectedPurchase.contact?.landmark || '14199 E HIGHWAY 12 ROGERS'}, <br />
                      {selectedPurchase.contact?.city || 'Little Rock'}, {selectedPurchase.contact?.state || 'AR'}, {selectedPurchase.contact?.country || 'USA'}
                    </div>
                  </div>
                  <div className="info-item">
                    <strong>Mobile:</strong> {selectedPurchase.contact?.mobile || 'N/A'}
                  </div>
                </div>
                <div className="service-info-box">
                  <div className="info-item">
                    <strong>Added By:</strong> {resolveAddedBy(selectedPurchase)}
                  </div>
                </div>
              </div>

              {/* Products Section */}
              <div className="invoice-section">
                <h3>Items Details:</h3>
                <div className="invoice-table-wrapper">
                  <table className="invoice-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product Name</th>
                        <th>SKU</th>
                        <th>Purchase Quantity</th>
                        <th>Unit Cost (Before Disc)</th>
                        <th>Discount Percent</th>
                        <th>Unit Cost (Before Tax)</th>
                        <th>Subtotal (Before Tax)</th>
                        <th>Tax</th>
                        <th>Unit Cost Price (After Tax)</th>
                        <th>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingView ? (
                        <tr>
                          <td colSpan="11" style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', flexDirection: 'column' }}>
                              <div className="spinner-loader" style={{ width: '1.75rem', height: '1.75rem', border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%' }}></div>
                              <span style={{ fontWeight: 500 }}>Loading item details...</span>
                            </div>
                          </td>
                        </tr>
                      ) : selectedPurchase.purchase_lines && selectedPurchase.purchase_lines.length > 0 ? (
                        selectedPurchase.purchase_lines.map((line, idx) => {
                          const qty = parseFloat(line.quantity || 0);
                          const priceBeforeDisc = parseFloat(line.pp_without_discount || line.purchase_price || 0);
                          const discPercent = parseFloat(line.discount_percent || 0);
                          const priceBeforeTax = parseFloat(line.purchase_price || 0);
                          const taxRate = parseFloat(line.product?.product_tax?.amount || line.tax_percent || 0);
                          const subtotalBeforeTax = priceBeforeTax * qty;
                          const taxAmt = subtotalBeforeTax * taxRate / 100;
                          const tax = parseFloat(line.item_tax || 0) || taxAmt;
                          const netCost = priceBeforeTax + (tax / (qty || 1));
                          const subtotal = priceBeforeTax * qty + tax;

                          return (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td style={{ fontWeight: 600 }}>{line.product?.name || `Product #${line.product_id}`}</td>
                              <td>{line.product?.sku || line.variations?.sub_sku || '--'}</td>
                              <td>{qty.toFixed(2)} {line.product?.unit?.short_name || 'Single'}</td>
                              <td>$ {priceBeforeDisc.toFixed(2)}</td>
                              <td>{discPercent.toFixed(2)} %</td>
                              <td>$ {priceBeforeTax.toFixed(2)}</td>
                              <td>$ {subtotalBeforeTax.toFixed(2)}</td>
                              <td>$ {tax.toFixed(2)}</td>
                              <td>$ {netCost.toFixed(2)}</td>
                              <td style={{ fontWeight: 700, color: '#2563eb' }}>$ {subtotal.toFixed(2)}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr><td colSpan="11" style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>No product items found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Payment and Totals Wrapper */}
              <div className="payment-totals-wrapper">
                <div className="invoice-section payment-info-section">
                  <h3>Payment Info:</h3>
                  <div className="invoice-table-wrapper">
                    <table className="invoice-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Date</th>
                          <th>Reference No</th>
                          <th>Amount</th>
                          <th>Payment Mode</th>
                          <th>Payment Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoadingView ? (
                          <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', flexDirection: 'column' }}>
                                <div className="spinner-loader" style={{ width: '1.75rem', height: '1.75rem', border: '3px solid #e2e8f0', borderTopColor: '#10b981', borderRadius: '50%' }}></div>
                                <span style={{ fontWeight: 500 }}>Loading payments...</span>
                              </div>
                            </td>
                          </tr>
                        ) : selectedPurchase.payment_lines && selectedPurchase.payment_lines.length > 0 ? (
                          selectedPurchase.payment_lines.map((pmt, pIdx) => (
                            <tr key={pIdx}>
                              <td>{pIdx + 1}</td>
                              <td>{
                                (() => {
                                  const d = pmt.paid_on;
                                  if (!d || d.startsWith('0000') || d === '--') return '--';
                                  const parsed = new Date(d);
                                  return isNaN(parsed.getTime()) ? d : parsed.toLocaleString();
                                })()
                              }</td>
                              <td>{pmt.payment_ref_no || '--'}</td>
                              <td style={{ fontWeight: 700, color: '#059669' }}>$ {parseFloat(pmt.amount || 0).toFixed(2)}</td>
                              <td>{pmt.method}</td>
                              <td>{pmt.note || '--'}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No payments found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Activities Section */}
                  <div style={{ marginTop: '2rem' }} className="invoice-section">
                    <h3>Activities:</h3>
                    <div className="invoice-table-wrapper">
                      <table className="invoice-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Action</th>
                            <th>By</th>
                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{selectedPurchase.transaction_date}</td>
                            <td>Added</td>
                            <td>{resolveAddedBy(selectedPurchase)}</td>
                            <td>Original purchase entry created.</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Totals Section */}
                {(() => {
                  const finalTotal    = parseFloat(selectedPurchase.final_total || 0);
                  const netBeforeTax  = parseFloat(selectedPurchase.total_before_tax || finalTotal);
                  const discountAmt   = parseFloat(selectedPurchase.discount_amount || 0);
                  const shippingAmt   = parseFloat(selectedPurchase.shipping_charges || 0);
                  const sumLineTax    = (selectedPurchase.purchase_lines || []).reduce((s, l) => {
                    const qty  = parseFloat(l.quantity || 0);
                    const rate = parseFloat(l.product?.product_tax?.amount || l.tax_percent || 0);
                    const stored = parseFloat(l.item_tax || 0);
                    return s + (stored > 0 ? stored : qty * parseFloat(l.purchase_price || 0) * rate / 100);
                  }, 0);
                  const storedPurchaseTax = parseFloat(selectedPurchase.tax_amount || 0);
                  const derivedPurchaseTax = Math.max(0, finalTotal - (netBeforeTax - discountAmt) - sumLineTax - shippingAmt);
                  const purchaseTaxDisplay = storedPurchaseTax > 0 ? storedPurchaseTax : derivedPurchaseTax;
                  return (
                <div className="totals-section">
                  <div className="totals-grid">
                    <div className="total-row">
                      <span className="total-label">Net Total Amount:</span>
                      <span className="total-value">$ {netBeforeTax.toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Discount:</span>
                      <span className="total-value">(-) $ {discountAmt.toFixed(2)}</span>
                    </div>
                    {sumLineTax > 0 && (
                      <div className="total-row">
                        <span className="total-label">Product Tax:</span>
                        <span className="total-value">(+) $ {sumLineTax.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="total-row">
                      <span className="total-label">Purchase Tax:</span>
                      <span className="total-value">(+) $ {purchaseTaxDisplay.toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Additional Shipping charges:</span>
                      <span className="total-value">(+) $ {parseFloat(selectedPurchase.shipping_charges || 0).toFixed(2)}</span>
                    </div>
                    <div className="total-row total-payable-row">
                      <span className="total-label">Purchase Total:</span>
                      <span className="total-value">$ {parseFloat(selectedPurchase.final_total || 0).toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Amount Paid:</span>
                      <span className="total-value" style={{ color: '#10b981' }}>$ { (parseFloat(selectedPurchase.final_total || 0) - parseFloat(selectedPurchase.payment_due || 0)).toFixed(2) }</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Payment Due:</span>
                      <span className="total-value" style={{ color: '#ef4444' }}>$ {parseFloat(selectedPurchase.payment_due || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                  );
                })()}
              </div>

              {/* Shipping Details */}
              <div className="invoice-section">
                <h3>Shipping Details:</h3>
                <div className="shipping-info-box" style={{ background: 'rgba(255,255,255,0.5)', border: '1px solid var(--etpos-border-light)', padding: '1.25rem', borderRadius: '8px' }}>
                  <div style={{ color: 'var(--etpos-text)', fontWeight: 500, lineHeight: '1.4' }}>
                    {selectedPurchase.shipping_details || '--'}
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="notes-section">
                <div className="note-item">
                  <h4>Additional Notes:</h4>
                  <p>{selectedPurchase.additional_notes || '--'}</p>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="modal-btn print-invoice-btn" onClick={() => handlePrint(selectedPurchase)}>
                🖨️ Print Purchase
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Label Generator Modal */}
      <LabelGenerator
        isOpen={showLabelModal}
        onClose={() => {
          setShowLabelModal(false);
          setProductsForLabels([]);
        }}
        initialProducts={productsForLabels}
      />

      {/* Add Payment Modal */}
      {showAddPaymentModal && selectedPurchase && (
        <AddPaymentModal
          sale={selectedPurchase}
          onClose={() => {
            setShowAddPaymentModal(false);
            setSelectedPurchase(null);
          }}
          onSuccess={() => {
            fetchPurchases();
          }}
        />
      )}

      {/* View Payments Modal */}
      {showViewPaymentsModal && selectedPurchase && (
        <ViewPaymentsModal
          show={showViewPaymentsModal}
          onClose={() => setShowViewPaymentsModal(false)}
          transaction={selectedPurchase}
          onPaymentDeleted={fetchPurchases}
        />
      )}

      {showUpdateStatusModal && selectedPurchase && (
        <UpdatePurchaseStatusModal
          show={showUpdateStatusModal}
          onClose={() => setShowUpdateStatusModal(false)}
          purchase={selectedPurchase}
          onSuccess={fetchPurchases}
        />
      )}

      {showNotificationModal && selectedPurchase && (
        <PurchaseNotificationModal
          purchase={selectedPurchase}
          onClose={() => setShowNotificationModal(false)}
          onSuccess={() => {}}
        />
      )}
    </div>
  </div>
);
};

export default Purchases;
